/**
 * Typed fetch client for the 1QA REST API.
 * - Adds the bearer token from localStorage.
 * - Normalises the `{ error: { code, message } }` error shape into ApiError.
 * - On 401 (outside public pages) clears the token and redirects to /login.
 */
import type { RequirementSetPatch, RequirementSetSummary, RequirementSetView, CodeRepoInput, CodeRepoView, DeviceProfile, PerfDataSetInput, PerfDataSetView, PerfFileView, RecordingInput, RecordingPerfScriptInput, RecordingStart, RecordingView, ServerCertInput, ServerCertView } from '@1qa/shared';
import type {
  AdminOrganization,
  AdminUser,
  AuditFilters,
  AuditPage,
  AuditSummary,
  CommentThreadView,
  Dashboard,
  ProjectMember,
  ReportCommentView,
  UnreadComments,
  LlmConfigView,
  LlmStatus,
  LlmTestResult,
  VariableView,
  AuthResponse,
  InvitationCreated,
  Invitation,
  InvitationPreview,
  Me,
  Member,
  Organization,
  Plugin,
  Project,
  Run,
  Share,
  TestCase,
  TestGroup,
  TestGroupInput,
  User,
  Flow,
  FlowInputBody,
  FlowListItem,
  FlowRunDetail,
  FlowRunListItem,
  Trigger,
  TriggerInputBody,
} from './types';
import type {
  CollectionInput,
  CollectionUpdate,
  CollectionView,
  LlmConfigCreate,
  LlmConfigUpdate,
  OrgRole,
  Platform,
  PlatformRole,
  TestStep,
  TestType,
  VariableCopy,
  VariableInput,
  VariableUpdate,
  RunnerStatus,
  AppBuildView,
  DeviceAgentView,
  LoadAgentView,
  LoadDecision,
  LoadLimits,
  PerfRunInput,
  PerfScript,
  ScriptCheckView,
  PerfConfig,
  PerfCaptureView,
  PerfReview,
  LoadShape,
  WorkloadModel,
  SecurityScope,
  ScanProfile,
  SecurityEngine,
} from '@1qa/shared';

export const API_BASE = '/api/v1';
const TOKEN_KEY = '1qa.token';

export const tokenStore = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Optional structured details, e.g. VariableProblem[] for INVALID_VARIABLES or missing fields. */
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const PUBLIC_PATHS = ['/login', '/invite/', '/shared/'];

function onUnauthorized() {
  tokenStore.clear();
  const path = window.location.pathname;
  if (!PUBLIC_PATHS.some((p) => path.startsWith(p))) {
    const next = encodeURIComponent(path + window.location.search);
    window.location.assign(`/login?next=${next}`);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the 1QA server. Check your connection and try again.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && path !== '/auth/login') onUnauthorized();
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? (res.status === 403 ? 'You do not have permission to do that.' : res.statusText || 'Request failed'),
      err?.details,
    );
  }
  return data as T;
}

/**
 * Sends a file as the raw request body (app builds), reporting upload progress (0..1), which fetch cannot.
 * Errors are ApiErrors like request()'s.
 */
export function uploadFile<T>(path: string, file: File, onProgress?: (fraction: number) => void, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API_BASE}${path}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    const token = tokenStore.get();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Cannot reach the 1QA server. Check your connection and try again.'));
    xhr.onload = () => {
      let data: unknown;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        data = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
      if (xhr.status === 401) onUnauthorized();
      const err = (data as { error?: { code?: string; message?: string } } | undefined)?.error;
      const fallback = xhr.status === 413 ? 'The file is too large to upload.' : xhr.statusText || 'Upload failed';
      reject(new ApiError(xhr.status, err?.code ?? `HTTP_${xhr.status}`, err?.message ?? fallback));
    };
    xhr.send(file);
  });
}

/** Accept either a bare array or a `{ items }` / `{ data }` envelope. */
export function asList<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    const v = value as { items?: unknown; data?: unknown };
    if (Array.isArray(v.items)) return v.items as T[];
    if (Array.isArray(v.data)) return v.data as T[];
  }
  return [];
}

/** Query string from the defined values only ("" when none). */
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false);
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : '';
}

const get = <T>(p: string) => request<T>('GET', p);
const list = async <T>(p: string) => asList<T>(await request<unknown>('GET', p));
const enc = encodeURIComponent;

export interface ProjectInput {
  name: string;
  description?: string;
  platform: Platform;
  targetUrl?: string;
  appId?: string;
  settings?: Record<string, unknown>;
}

export interface TestInput {
  name: string;
  description?: string;
  type?: TestType;
  steps: TestStep[];
  tags?: string[];
  /** PERFORMANCE tests: the load plan (see createPerfTest). */
  perfConfig?: PerfConfig;
}

export const api = {
  // auth
  login: (email: string, password: string) => request<AuthResponse>('POST', '/auth/login', { email, password }),
  me: () => get<Me>('/auth/me'),
  invitationPreview: (token: string) => get<InvitationPreview>(`/invitations/${enc(token)}`),
  acceptInvitation: (token: string, name: string, password: string) =>
    request<AuthResponse>('POST', `/invitations/${enc(token)}/accept`, { name, password }),

  // admin
  adminUsers: () => list<AdminUser>('/admin/users'),
  adminOrgs: () => list<AdminOrganization>('/admin/orgs'),
  adminUpdateUser: (id: string, patch: { platformRole?: PlatformRole; status?: User['status'] }) =>
    request<User>('PATCH', `/admin/users/${enc(id)}`, patch),
  adminInvitations: () => list<Invitation>('/admin/invitations'),
  adminInvite: (email: string, platformRole: 'PROJECT_ADMIN' | 'ADMIN') =>
    request<InvitationCreated>('POST', '/admin/invitations', { email, platformRole }),
  adminRevokeInvitation: (id: string) => request<void>('DELETE', `/admin/invitations/${enc(id)}`),
  adminAudit: (filters: AuditFilters & { cursor?: string; limit?: number } = {}) =>
    get<AuditPage>(`/admin/audit${qs({ ...filters })}`),
  adminAuditSummary: (days: number) => get<AuditSummary>(`/admin/audit/summary${qs({ days })}`),

  // plugins (Project Admin; organization-wide or per project)
  plugins: (orgId: string) => list<Plugin>(`/orgs/${enc(orgId)}/plugins`),
  upsertPlugin: (
    orgId: string,
    key: string,
    /** `secret` (notification plugins): set it, null to remove it, omit to keep the saved one. */
    body: Pick<Plugin, 'name' | 'enabled' | 'config'> & { secretRef?: string | null; projectId?: string | null; secret?: string | null },
  ) => request<Plugin>('PUT', `/orgs/${enc(orgId)}/plugins/${enc(key)}`, body),
  deletePlugin: (id: string) => request<void>('DELETE', `/plugins/${enc(id)}`),
  testPlugin: (id: string) => request<{ ok: boolean }>('POST', `/plugins/${enc(id)}/test`, {}),
  /** Notification channels in effect for a project's schedules and webhooks. */
  notificationChannels: (projectId: string) => list<{ key: string; name: string; recipients?: string[] }>(`/projects/${enc(projectId)}/notification-channels`),

  // AI models (Project Admin; organization-wide or per project)
  llmConfigs: (orgId: string) => list<LlmConfigView>(`/orgs/${enc(orgId)}/llm-configs`),
  createLlmConfig: (orgId: string, body: LlmConfigCreate) => request<LlmConfigView>('POST', `/orgs/${enc(orgId)}/llm-configs`, body),
  updateLlmConfig: (id: string, body: LlmConfigUpdate) => request<LlmConfigView>('PATCH', `/llm-configs/${enc(id)}`, body),
  makeDefaultLlmConfig: (id: string) => request<LlmConfigView>('POST', `/llm-configs/${enc(id)}/default`),
  testLlmConfig: (id: string) => request<LlmTestResult>('POST', `/llm-configs/${enc(id)}/test`),
  deleteLlmConfig: (id: string) => request<void>('DELETE', `/llm-configs/${enc(id)}`),
  llmStatus: (projectId: string) => get<LlmStatus>(`/projects/${enc(projectId)}/llm-status`),

  // test data (variables)
  orgVariables: (orgId: string) => list<VariableView>(`/orgs/${enc(orgId)}/variables`),
  createOrgVariable: (orgId: string, body: VariableInput) => request<VariableView>('POST', `/orgs/${enc(orgId)}/variables`, body),
  projectVariables: (projectId: string) => list<VariableView>(`/projects/${enc(projectId)}/variables`),
  createProjectVariable: (projectId: string, body: VariableInput) =>
    request<VariableView>('POST', `/projects/${enc(projectId)}/variables`, body),
  updateVariable: (id: string, body: VariableUpdate) => request<VariableView>('PATCH', `/variables/${enc(id)}`, body),
  deleteVariable: (id: string) => request<void>('DELETE', `/variables/${enc(id)}`),
  copyVariable: (id: string, body: VariableCopy) => request<VariableView>('POST', `/variables/${enc(id)}/copy`, body),

  // test data collections (Dev, Staging, ...)
  orgCollections: (orgId: string) => list<CollectionView>(`/orgs/${enc(orgId)}/collections`),
  createOrgCollection: (orgId: string, body: CollectionInput) => request<CollectionView>('POST', `/orgs/${enc(orgId)}/collections`, body),
  projectCollections: (projectId: string) => list<CollectionView>(`/projects/${enc(projectId)}/collections`),
  createProjectCollection: (projectId: string, body: CollectionInput) =>
    request<CollectionView>('POST', `/projects/${enc(projectId)}/collections`, body),
  updateCollection: (id: string, body: CollectionUpdate) => request<CollectionView>('PATCH', `/collections/${enc(id)}`, body),
  deleteCollection: (id: string) => request<void>('DELETE', `/collections/${enc(id)}`),
  copyCollection: (id: string, body: CollectionInput) => request<CollectionView>('POST', `/collections/${enc(id)}/copy`, body),

  // orgs
  orgs: () => list<Organization>('/orgs'),
  createOrg: (name: string) => request<Organization>('POST', '/orgs', { name }),
  org: (orgId: string) => get<Organization>(`/orgs/${enc(orgId)}`),
  updateOrg: (orgId: string, name: string) => request<Organization>('PATCH', `/orgs/${enc(orgId)}`, { name }),
  members: (orgId: string) => list<Member>(`/orgs/${enc(orgId)}/members`),
  updateMember: (orgId: string, userId: string, role: OrgRole) =>
    request<Member>('PATCH', `/orgs/${enc(orgId)}/members/${enc(userId)}`, { role }),
  removeMember: (orgId: string, userId: string) => request<void>('DELETE', `/orgs/${enc(orgId)}/members/${enc(userId)}`),
  orgInvitations: (orgId: string) => list<Invitation>(`/orgs/${enc(orgId)}/invitations`),
  inviteMember: (orgId: string, email: string, role: OrgRole, projectIds: string[] = []) =>
    request<InvitationCreated>('POST', `/orgs/${enc(orgId)}/invitations`, { email, role, projectIds }),

  // app builds (mobile projects)
  builds: (projectId: string) => list<AppBuildView>(`/projects/${enc(projectId)}/builds`),
  uploadBuild: (projectId: string, file: File, notes: string, onProgress?: (fraction: number) => void) =>
    uploadFile<AppBuildView>(`/projects/${enc(projectId)}/builds${qs({ fileName: file.name, notes: notes.trim() || undefined })}`, file, onProgress),
  deleteBuild: (buildId: string) => request<void>('DELETE', `/builds/${enc(buildId)}`),

  // device agents (phones on testers' computers)
  deviceAgents: (orgId: string) => list<DeviceAgentView>(`/orgs/${enc(orgId)}/device-agents`),
  createDeviceAgent: (orgId: string, name: string) => request<{ agent: DeviceAgentView; token: string }>('POST', `/orgs/${enc(orgId)}/device-agents`, { name }),
  updateDeviceAgent: (agentId: string, body: { name?: string; paused?: boolean }) => request<DeviceAgentView>('PATCH', `/device-agents/${enc(agentId)}`, body),
  deleteDeviceAgent: (agentId: string) => request<void>('DELETE', `/device-agents/${enc(agentId)}`),

  // recording on a phone (docs/design/mobile-recorder.md)
  startRecording: (projectId: string, body: RecordingStart) => request<RecordingView>('POST', `/projects/${enc(projectId)}/recordings`, body),
  recording: (recordingId: string, after: number) => get<RecordingView>(`/recordings/${enc(recordingId)}${qs({ after })}`),
  stopRecording: (recordingId: string) => request<{ ok: true }>('POST', `/recordings/${enc(recordingId)}/stop`, {}),
  cancelRecording: (recordingId: string) => request<void>('DELETE', `/recordings/${enc(recordingId)}`),
  /** 1QA's emulators that can record performance scripts (connected Android runners). */
  recordingEmulators: (projectId: string) => get<DeviceProfile[]>(`/projects/${enc(projectId)}/recordings/emulators`),
  /** A tap, swipe, typing or key on an emulator recording's screen. */
  recordingInput: (recordingId: string, input: RecordingInput) => request<{ ok: true }>('POST', `/recordings/${enc(recordingId)}/input`, input),
  /** A performance project's server certificates (phones record those hosts with nothing installed); keys never come back. */
  serverCerts: (projectId: string) => get<ServerCertView[]>(`/projects/${enc(projectId)}/perf/server-certs`),
  addServerCert: (projectId: string, body: ServerCertInput) => request<ServerCertView>('POST', `/projects/${enc(projectId)}/perf/server-certs`, body),
  deleteServerCert: (certId: string) => request<void>('DELETE', `/perf/server-certs/${enc(certId)}`),
  /** A stopped performance recording -> a capture in a performance project (its review, or straight a script). */
  recordingPerfScript: (recordingId: string, body: RecordingPerfScriptInput) => request<PerfCaptureView>('POST', `/recordings/${enc(recordingId)}/perf-script`, body),

  // load agents (performance tests) and project load limits
  loadAgents: (orgId: string) => list<LoadAgentView>(`/orgs/${enc(orgId)}/load-agents`),
  createLoadAgent: (orgId: string, body: { name: string; bandwidthMbit?: number; projectIds?: string[] }) =>
    request<{ agent: LoadAgentView; token: string }>('POST', `/orgs/${enc(orgId)}/load-agents`, body),
  updateLoadAgent: (agentId: string, body: { name?: string; paused?: boolean; bandwidthMbit?: number; projectIds?: string[] }) =>
    request<LoadAgentView>('PATCH', `/load-agents/${enc(agentId)}`, body),
  deleteLoadAgent: (agentId: string) => request<void>('DELETE', `/load-agents/${enc(agentId)}`),
  probeLoadAgent: (agentId: string) => request<{ queued: boolean }>('POST', `/load-agents/${enc(agentId)}/probe`, {}),
  loadLimits: (projectId: string) => get<LoadLimitsView>(`/projects/${enc(projectId)}/load-limits`),
  setLoadLimits: (projectId: string, limits: LoadLimits) => request<LoadLimitsView>('PUT', `/projects/${enc(projectId)}/load-limits`, limits),
  perfCapacity: (testId: string, load?: LoadShape) => get<PerfCapacityView>(`/tests/${enc(testId)}/perf/capacity${load ? qs({ load: JSON.stringify(load) }) : ''}`),
  perfImport: (projectId: string, body: { har: unknown; har2?: unknown; origins?: string[]; keepStatic?: boolean; backendOnly?: boolean }) =>
    request<PerfImportResult>('POST', `/projects/${enc(projectId)}/perf/import`, body),
  /** Performance test from a functional test: a web runner runs it twice and records its traffic. */
  startPerfCapture: (projectId: string, body: { testCaseId: string; collectionId?: string | null; origins?: string[]; keepStatic?: boolean }) =>
    request<PerfCaptureView>('POST', `/projects/${enc(projectId)}/perf/captures`, body),
  perfCapture: (captureId: string) => get<PerfCaptureView>(`/perf/captures/${enc(captureId)}`),
  createPerfTest: (projectId: string, body: { name: string; description?: string; perfConfig: PerfConfig }) =>
    request<TestCase>('POST', `/projects/${enc(projectId)}/tests`, { ...body, type: 'PERFORMANCE' }),
  // the project's performance script library (journeys performance tests use in their scenarios)
  perfScripts: (projectId: string) => get<LoadScriptRow[]>(`/projects/${enc(projectId)}/perf/scripts`),
  perfScript: (scriptId: string) => get<LoadScriptRow & { script: PerfScript }>(`/perf/scripts/${enc(scriptId)}`),
  createPerfScript: (projectId: string, body: { name: string; description?: string | null; script: PerfScript; source?: LoadScriptRow['source']; sourceTestId?: string | null }) =>
    request<LoadScriptRow>('POST', `/projects/${enc(projectId)}/perf/scripts`, body),
  updatePerfScript: (scriptId: string, body: { name?: string; description?: string | null; script?: PerfScript }) => request<LoadScriptRow>('PATCH', `/perf/scripts/${enc(scriptId)}`, body),
  /** Script check: one user runs the saved script once (step by step), on a free load agent. */
  checkPerfScript: (scriptId: string, collectionId: string | null, dataSetId?: string | null) =>
    request<ScriptCheckView>('POST', `/perf/scripts/${enc(scriptId)}/check`, { ...(collectionId ? { collectionId } : {}), ...(dataSetId ? { dataSetId } : {}) }),
  // performance test data files (CSV / Excel rows, one per journey); the rows never come back, only a masked preview
  perfDataSets: (projectId: string) => get<PerfDataSetView[]>(`/projects/${enc(projectId)}/perf/data-sets`),
  // performance test files (images and other files scripts upload)
  perfFiles: (projectId: string) => get<PerfFileView[]>(`/projects/${enc(projectId)}/perf/files`),
  uploadPerfFile: (projectId: string, file: File, name: string, onProgress?: (fraction: number) => void) =>
    uploadFile<PerfFileView>(`/projects/${enc(projectId)}/perf/files${qs({ name, type: file.type || undefined })}`, file, onProgress),
  replacePerfFile: (fileId: string, file: File, onProgress?: (fraction: number) => void) =>
    uploadFile<PerfFileView>(`/perf/files/${enc(fileId)}${qs({ type: file.type || undefined })}`, file, onProgress, 'PUT'),
  deletePerfFile: (fileId: string) => request<void>('DELETE', `/perf/files/${enc(fileId)}`),
  createPerfDataSet: (projectId: string, body: PerfDataSetInput) => request<PerfDataSetView>('POST', `/projects/${enc(projectId)}/perf/data-sets`, body),
  replacePerfDataSet: (dataSetId: string, body: PerfDataSetInput) => request<PerfDataSetView>('PUT', `/perf/data-sets/${enc(dataSetId)}`, body),
  deletePerfDataSet: (dataSetId: string) => request<void>('DELETE', `/perf/data-sets/${enc(dataSetId)}`),
  perfScriptChecks: (scriptId: string) => get<ScriptCheckView[]>(`/perf/scripts/${enc(scriptId)}/checks`),
  copyPerfScript: (scriptId: string) => request<LoadScriptRow>('POST', `/perf/scripts/${enc(scriptId)}/copy`, {}),
  deletePerfScript: (scriptId: string) => request<void>('DELETE', `/perf/scripts/${enc(scriptId)}`),
  /** A performance run: the test alone; `perf` picks agents / workload, or a 1-user check (`verify`). */
  triggerPerfRun: (projectId: string, testId: string, perf: PerfRunInput, collectionId?: string | null) =>
    request<Run>('POST', `/projects/${enc(projectId)}/runs`, { testCaseIds: [testId], perf, ...(collectionId ? { collectionId } : {}) }),

  // security scanning (SECURITY tests in security projects; scope-gated)
  securityScope: (projectId: string) => get<SecurityScope>(`/projects/${enc(projectId)}/security/scope`),
  updateSecurityScope: (projectId: string, body: { authorizedHosts: string[]; allowProduction: boolean; rulesOfEngagement: string; attested: boolean }) =>
    request<SecurityScope>('PUT', `/projects/${enc(projectId)}/security/scope`, body),
  /** The code repository the code-level security audit reads (null: none connected). The token is never returned. */
  codeRepo: (projectId: string) => get<CodeRepoView | null>(`/projects/${enc(projectId)}/security/repo`),
  /** Connect / change it; the API checks it can be read first. `token`: undefined keeps the stored one, '' removes it. */
  connectCodeRepo: (projectId: string, body: CodeRepoInput) =>
    request<CodeRepoView & { branches: string[] }>('PUT', `/projects/${enc(projectId)}/security/repo`, body),
  checkCodeRepo: (projectId: string) =>
    request<{ ok: boolean; message?: string; branches?: string[]; defaultBranch?: string | null }>('POST', `/projects/${enc(projectId)}/security/repo/check`, {}),
  disconnectCodeRepo: (projectId: string) => request<void>('DELETE', `/projects/${enc(projectId)}/security/repo`),
  createSecurityTest: (projectId: string, body: { name: string; description?: string }) =>
    request<TestCase>('POST', `/projects/${enc(projectId)}/tests`, { ...body, type: 'SECURITY' }),
  /** A security scan: the test alone; `profile` picks Quick (baseline) or Deep (full exploit run), `engine` the pentester. */
  triggerSecurityRun: (projectId: string, testId: string, profile: ScanProfile, engine: SecurityEngine, collectionId?: string | null) =>
    request<Run>('POST', `/projects/${enc(projectId)}/runs`, { testCaseIds: [testId], security: { profile, engine }, ...(collectionId ? { collectionId } : {}) }),

  // runners: connected runners per platform (counts only)
  runnerStatus: () => get<RunnerStatus>('/runners/status'),

  // projects
  projects: (orgId: string) => list<Project>(`/orgs/${enc(orgId)}/projects`),
  createProject: (orgId: string, body: ProjectInput) => request<Project>('POST', `/orgs/${enc(orgId)}/projects`, body),
  project: (projectId: string) => get<Project>(`/projects/${enc(projectId)}`),
  updateProject: (projectId: string, body: Partial<ProjectInput>) =>
    request<Project>('PATCH', `/projects/${enc(projectId)}`, body),
  deleteProject: (projectId: string) => request<void>('DELETE', `/projects/${enc(projectId)}`),
  projectMembers: (projectId: string) => list<ProjectMember>(`/projects/${enc(projectId)}/members`),
  assignProjectMember: (projectId: string, userId: string) =>
    request<{ userId: string }>('PUT', `/projects/${enc(projectId)}/members/${enc(userId)}`),
  unassignProjectMember: (projectId: string, userId: string) =>
    request<void>('DELETE', `/projects/${enc(projectId)}/members/${enc(userId)}`),

  // tests
  tests: (projectId: string) => list<TestCase>(`/projects/${enc(projectId)}/tests`),
  createTest: (projectId: string, body: TestInput) => request<TestCase>('POST', `/projects/${enc(projectId)}/tests`, body),
  test: (testId: string) => get<TestCase>(`/tests/${enc(testId)}`),
  updateTest: (testId: string, body: Partial<TestInput>) => request<TestCase>('PATCH', `/tests/${enc(testId)}`, body),
  deleteTest: (testId: string) => request<void>('DELETE', `/tests/${enc(testId)}`),
  copyTest: (testId: string) => request<TestCase>('POST', `/tests/${enc(testId)}/copy`, {}),

  // groups
  groups: (projectId: string) => list<TestGroup>(`/projects/${enc(projectId)}/groups`),
  createGroup: (projectId: string, body: TestGroupInput) => request<TestGroup>('POST', `/projects/${enc(projectId)}/groups`, body),
  updateGroup: (groupId: string, body: Partial<TestGroupInput>) => request<TestGroup>('PATCH', `/groups/${enc(groupId)}`, body),
  deleteGroup: (groupId: string) => request<void>('DELETE', `/groups/${enc(groupId)}`),
  runGroup: (groupId: string, opts: { liveView?: boolean; collectionId?: string | null; appBuildId?: string | null; deviceProfile?: string | null } = {}) =>
    request<Run>('POST', `/groups/${enc(groupId)}/runs`, {
      ...(opts.liveView ? { liveView: true } : {}),
      ...(opts.collectionId ? { collectionId: opts.collectionId } : {}),
      ...(opts.appBuildId ? { appBuildId: opts.appBuildId } : {}),
      ...(opts.deviceProfile ? { deviceProfile: opts.deviceProfile } : {}),
    }),

  // flows
  flows: (projectId: string) => list<FlowListItem>(`/projects/${enc(projectId)}/flows`),
  flow: (flowId: string) => get<Flow>(`/flows/${enc(flowId)}`),
  createFlow: (projectId: string, body: FlowInputBody) => request<Flow>('POST', `/projects/${enc(projectId)}/flows`, body),
  updateFlow: (flowId: string, body: Partial<FlowInputBody>) => request<Flow>('PATCH', `/flows/${enc(flowId)}`, body),
  deleteFlow: (flowId: string) => request<void>('DELETE', `/flows/${enc(flowId)}`),
  runFlow: (flowId: string, opts: { collectionId?: string | null; appBuildId?: string | null; deviceProfile?: string | null } = {}) =>
    request<FlowRunListItem>('POST', `/flows/${enc(flowId)}/runs`, {
      ...(opts.collectionId ? { collectionId: opts.collectionId } : {}),
      ...(opts.appBuildId ? { appBuildId: opts.appBuildId } : {}),
      ...(opts.deviceProfile ? { deviceProfile: opts.deviceProfile } : {}),
    }),
  flowRuns: (projectId: string, flowId?: string) =>
    list<FlowRunListItem>(`/projects/${enc(projectId)}/flow-runs${flowId ? `?flowId=${enc(flowId)}` : ''}`),
  flowRun: (flowRunId: string) => get<FlowRunDetail>(`/flow-runs/${enc(flowRunId)}`),
  cancelFlowRun: (flowRunId: string) => request<{ ok: boolean }>('POST', `/flow-runs/${enc(flowRunId)}/cancel`, {}),

  // schedules & webhooks
  triggers: (projectId: string) => list<Trigger>(`/projects/${enc(projectId)}/triggers`),
  createTrigger: (projectId: string, body: TriggerInputBody) => request<Trigger>('POST', `/projects/${enc(projectId)}/triggers`, body),
  updateTrigger: (triggerId: string, body: Partial<Omit<TriggerInputBody, 'kind'>>) => request<Trigger>('PATCH', `/triggers/${enc(triggerId)}`, body),
  deleteTrigger: (triggerId: string) => request<void>('DELETE', `/triggers/${enc(triggerId)}`),
  rotateTriggerToken: (triggerId: string) => request<Trigger>('POST', `/triggers/${enc(triggerId)}/token`, {}),

  // runs & reports
  /**
   * Starts a run. `liveView` is opt-in and only sent when on (Chromium web projects); no collection = default test
   * data; no app build = the project's latest (mobile).
   */
  triggerRun: (
    projectId: string,
    testCaseIds?: string[],
    opts: { liveView?: boolean; collectionId?: string | null; appBuildId?: string | null; deviceProfile?: string | null } = {},
  ) =>
    request<Run>('POST', `/projects/${enc(projectId)}/runs`, {
      ...(testCaseIds ? { testCaseIds } : {}),
      ...(opts.liveView ? { liveView: true } : {}),
      ...(opts.collectionId ? { collectionId: opts.collectionId } : {}),
      ...(opts.appBuildId ? { appBuildId: opts.appBuildId } : {}),
      ...(opts.deviceProfile ? { deviceProfile: opts.deviceProfile } : {}),
    }),
  projectRuns: (projectId: string) => list<Run>(`/projects/${enc(projectId)}/runs`),
  run: (runId: string) => get<Run>(`/runs/${enc(runId)}`),
  cancelRun: (runId: string) => request<Run>('POST', `/runs/${enc(runId)}/cancel`),
  deleteRun: (runId: string) => request<void>('DELETE', `/runs/${enc(runId)}`),
  deleteArtifact: (resultId: string, name: string) => request<void>('DELETE', `/artifacts/${enc(resultId)}/${enc(name)}`),
  reports: () => list<Run>('/reports'),
  // tests from business documents (BRD)
  requirementSets: (projectId: string) => list<RequirementSetSummary>(`/projects/${enc(projectId)}/requirement-sets`),
  uploadRequirements: (projectId: string, body: { name: string; files: { name: string; data: string }[]; text?: string; notes?: string }) =>
    request<RequirementSetView>('POST', `/projects/${enc(projectId)}/requirement-sets`, body),
  requirementSet: (setId: string) => get<RequirementSetView>(`/requirement-sets/${enc(setId)}`),
  updateRequirementSet: (setId: string, patch: RequirementSetPatch) => request<RequirementSetView>('PATCH', `/requirement-sets/${enc(setId)}`, patch),
  deleteRequirementSet: (setId: string) => request<void>('DELETE', `/requirement-sets/${enc(setId)}`),
  analyzeRequirements: (setId: string) => request<RequirementSetView>('POST', `/requirement-sets/${enc(setId)}/analyze`, {}),
  generateDrafts: (setId: string, body: { keys?: string[]; replace?: boolean } = {}) =>
    request<RequirementSetView>('POST', `/requirement-sets/${enc(setId)}/generate`, body),
  saveDrafts: (setId: string, body: { draftIds: string[]; groupName?: string }) =>
    request<{
      saved: { draftId: string; testId: string; name: string }[];
      problems: { draftId: string; name: string; message: string }[];
      group: { id: string; name: string } | null;
      set: RequirementSetView;
    }>('POST', `/requirement-sets/${enc(setId)}/save`, body),
  dashboard: (orgId: string) => get<Dashboard>(`/orgs/${enc(orgId)}/dashboard`),
  shares: (runId: string) => list<Share>(`/runs/${enc(runId)}/shares`),
  shareWithUser: (runId: string, email: string) => request<Share>('POST', `/runs/${enc(runId)}/shares`, { email }),
  sharePublic: (runId: string, expiresInDays?: number) =>
    request<Share>('POST', `/runs/${enc(runId)}/shares`, { public: true, expiresInDays }),
  deleteShare: (shareId: string) => request<void>('DELETE', `/shares/${enc(shareId)}`),
  sharedReport: (token: string) => get<Run>(`/shared/${enc(token)}`),

  // report comments (private: share recipient <-> the member who shared)
  comments: (runId: string) => list<CommentThreadView>(`/runs/${enc(runId)}/comments`),
  addComment: (shareId: string, body: string) => request<ReportCommentView>('POST', `/shares/${enc(shareId)}/comments`, { body }),
  unreadComments: () => get<UnreadComments>('/comments/unread'),
};

/** Downloads the filtered audit log as CSV (needs the bearer token, so not a plain link). */
export async function downloadAuditCsv(filters: AuditFilters): Promise<void> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/admin/audit/export${qs({ ...filters })}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, 'Could not export the audit log');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `1qa-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** A performance test file's content (for previews): like artifacts, fetched with the sign-in header. */
export async function fetchPerfFileBlob(fileId: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/perf/files/${enc(fileId)}/content`, { headers });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, 'File unavailable');
  return res.blob();
}

/** A recorded tap's screenshot (phone recording review), fetched with the sign-in header. */
export async function fetchRecordingShot(recordingId: string, shotId: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/recordings/${enc(recordingId)}/shots/${enc(shotId)}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, 'Screenshot unavailable');
  return res.blob();
}

/** Artifacts require auth, so they are fetched as blobs rather than used as plain <img src>. */
export async function fetchArtifactBlob(resultId: string, name: string, publicToken?: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const qs = publicToken ? `?share=${enc(publicToken)}` : '';
  const res = await fetch(`${API_BASE}/artifacts/${enc(resultId)}/${enc(name)}${qs}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, 'Artifact unavailable');
  return res.blob();
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

/** Server-Sent Events endpoint of a run started with live view. */
export const liveStreamUrl = (runId: string) => `${API_BASE}/runs/${encodeURIComponent(runId)}/live`;

/**
 * Opens the live view stream with the bearer token (EventSource cannot send headers). Resolves to the
 * response; throws ApiError for HTTP errors (409 LIVE_VIEW_OFF, 404, ...).
 */
export function openLiveStream(runId: string, signal: AbortSignal): Promise<Response> {
  return openEventStream(liveStreamUrl(runId), signal);
}

/** An emulator recording's screen (server-sent `frame` events, then `end`), with the bearer token. */
export function openRecordingScreen(recordingId: string, signal: AbortSignal): Promise<Response> {
  return openEventStream(`${API_BASE}/recordings/${encodeURIComponent(recordingId)}/screen`, signal);
}

async function openEventStream(url: string, signal: AbortSignal): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers, signal, cache: 'no-store' });
  if (!res.ok || !res.body) {
    if (res.status === 401) onUnauthorized();
    let code = `HTTP_${res.status}`;
    let message = res.statusText || 'Live view is not available';
    try {
      const err = (JSON.parse(await res.text()) as { error?: { code?: string; message?: string } }).error;
      if (err?.code) code = err.code;
      if (err?.message) message = err.message;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, code, message);
  }
  return res;
}

export interface LoadLimitsView {
  limits: LoadLimits;
  memberLimit: { vus: number; durationS: number };
  /** False: the defaults (no Project Admin has set limits yet). */
  custom: boolean;
}

/** One agent's capacity estimate for a test (CapacityEstimate in @1qa/perf). */
export interface CapacityEstimateView {
  vus: number;
  rps: number;
  limitedBy: 'memory' | 'cpu' | 'network' | 'sockets';
  limits: Record<'memory' | 'cpu' | 'network' | 'sockets', number>;
  /** The agent has not measured its machine yet: planning defaults. */
  estimated: boolean;
}

export interface PerfCapacityView {
  agents: { id: string; name: string; available: boolean; unavailable: string | null; estimate: CapacityEstimateView | null }[];
  /** Users the free agents can carry for this test, together. */
  estimateVus: number;
  /** The whole test as one workload (its scenarios added up). */
  model: WorkloadModel;
  limits: LoadLimits;
  memberLimit: { vus: number; durationS: number };
  decision: LoadDecision;
}

/** A script of the project's library (GET /projects/:id/perf/scripts; the IR itself only from GET /perf/scripts/:id). */
export interface LoadScriptRow {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  source: 'har' | 'recorder' | 'functional' | 'copy';
  sourceTestId: string | null;
  transactions: number;
  requests: number;
  websockets: number;
  /** Performance tests whose scenarios use it. */
  usedBy: { id: string; name: string }[];
  createdById: string;
  createdBy?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** What 1QA found in a recording (HAR import or a recorded functional test), with the script's problems. */
export type PerfImportResult = PerfReview & { problems: string[]; appUrl: string; missingData?: string[]; missingFiles?: string[] };
